import useUpdateBillingDetailsMutation from '@/api/mutations/useUpdateBillingDetailsMutation';
import useGetBillingDetailsQuery from '@/api/queries/useGetBillingDetailsQuery';
import CustomToast from '@/components/CustomToast';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import BillingDetailsForm, {
  BillingDetailsFormData,
} from '@/modules/billing/components/BillingDetailsForm';
import { BillingAddress, BillingDetails } from '@/modules/billing/models/Billing';
import { BillingHomePath } from '@/modules/billing/paths';
import SettingsHeader from '@/modules/settings/components/SettingsHeader';
import { CreditCardIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const toAddress = (values: BillingDetailsFormData, addressId?: string): BillingAddress => ({
  id: addressId,
  building: values.building.trim(),
  street1: values.street1.trim(),
  street2: values.street2.trim(),
  postalCode: values.postalCode.trim(),
  region: values.region.trim(),
  city: values.city.trim(),
  countryCode: values.countryCode,
});

const BillingDetailsEdit = () => {
  const navigate = useNavigate();
  const updateBillingDetails = useUpdateBillingDetailsMutation();
  const { billingDetails, isLoading, error } = useGetBillingDetailsQuery();

  const onSubmit = async (values: BillingDetailsFormData) => {
    const payload: BillingDetails = {
      companyName: values.companyName.trim(),
      billingEmail: values.billingEmail.trim(),
      taxId: values.taxId.trim() || null,
      address: toAddress(values, billingDetails?.address?.id),
    };

    try {
      await updateBillingDetails.mutateAsync(payload);

      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message={'Billing details updated successfully!'}
          visible={t.visible}
        />
      ));

      navigate(BillingHomePath);
    } catch (submitError) {
      console.error('Failed to update billing details:', submitError);
    }
  };

  if (isLoading) {
    return (
      <>
        <SettingsHeader
          title="Edit Billing Details"
          icon={<CreditCardIcon className="w-6 h-6" />}
        />
        <div className="flex justify-center py-20">
          <SpinnerIcon />
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <SettingsHeader
          title="Edit Billing Details"
          icon={<CreditCardIcon className="w-6 h-6" />}
        />
        <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">
          Failed to load billing details.
        </div>
      </>
    );
  }

  return (
    <>
      <SettingsHeader title="Edit Billing Details" icon={<CreditCardIcon className="w-6 h-6" />} />
      <BillingDetailsForm
        billingDetails={billingDetails}
        loading={updateBillingDetails.isPending}
        onSubmit={onSubmit}
        onCancel={() => navigate(BillingHomePath)}
      />
    </>
  );
};

export default BillingDetailsEdit;
